import { computed, signal } from '@preact/signals-react';
import type { ConversationMessage } from '@/embedded/chat/types/conversation-message';
import type {
  WebAgentConversationInfo,
  WebAgentConversationParticipant,
} from '@/website/lib/chat-api';
import type { ActivityLiveDataset } from './activity-live-dataset';
import { Conversation } from './conversation';
import type { ConversationLiveDataset } from './conversation-live-dataset';
import {
  normalizeText,
  sortConversations,
  uniqueNormalizedIds,
} from './conversation-utils';

export class ConversationCollection {
  private readonly recordRegistry = new Map<string, Conversation>();
  private readonly shellIds = signal(new Set<string>());
  readonly recordsById = computed(() => {
    const next = new Map<string, Conversation>();
    for (const conversationId of this.datasetConversationIds()) {
      next.set(conversationId, this.recordFor(conversationId));
    }
    return next;
  });

  constructor(
    private readonly conversationData: ConversationLiveDataset,
    private readonly activityData: ActivityLiveDataset
  ) {}

  readonly byId = computed(() => {
    const next = new Map<string, WebAgentConversationInfo>();
    for (const conversation of this.recordsById.value.values()) {
      if (!conversation.deleted.value && conversation.hasInfo.value) {
        next.set(conversation.id.value, conversation.info.value);
      }
    }
    return next;
  });

  readonly deletedIds = computed(() => {
    const next = new Set<string>();
    for (const conversation of this.recordsById.value.values()) {
      if (conversation.deleted.value) {
        next.add(conversation.id.value);
      }
    }
    return next;
  });

  readonly list = computed(() =>
    this.visibleListRecords().map(conversation => conversation.info.value)
  );

  readonly listItems = computed(() =>
    this.visibleListRecords().map(
      conversation => conversation.listItem.value
    )
  );

  readonly participantsByConversationId = computed(() => {
    const next = new Map<string, WebAgentConversationParticipant[]>();
    for (const conversation of this.recordsById.value.values()) {
      if (!conversation.deleted.value && conversation.hasInfo.value) {
        next.set(conversation.id.value, conversation.participants.value);
      }
    }
    return next;
  });

  readonly messagesByConversationId = computed(() => {
    const next = new Map<string, ConversationMessage[]>();
    for (const conversation of this.recordsById.value.values()) {
      if (!conversation.deleted.value) {
        next.set(conversation.id.value, conversation.messages.messages.value);
      }
    }
    return next;
  });

  get(conversationId: string | null | undefined) {
    const record = this.getRecord(conversationId);
    return record && !record.deleted.value && record.hasInfo.value
      ? record.info.value
      : null;
  }

  getRecord(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? (this.recordsById.value.get(normalizedConversationId) ?? null)
      : null;
  }

  listConversationIdFor(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    if (!normalizedConversationId) return null;
    const current = this.recordsById.value.get(normalizedConversationId);
    return current && current.hasInfo.value ? normalizedConversationId : null;
  }

  ensureShell(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    if (!normalizedConversationId) return null;
    const existing = this.recordsById.value.get(normalizedConversationId);
    if (existing) return existing;

    const conversation = this.recordFor(normalizedConversationId);
    const shellIds = new Set(this.shellIds.value);
    shellIds.add(normalizedConversationId);
    this.shellIds.value = shellIds;
    return conversation;
  }

  reset() {
    for (const conversation of this.recordRegistry.values()) {
      conversation.dispose();
    }
    this.recordRegistry.clear();
    this.shellIds.value = new Set();
  }

  dispose() {
    this.reset();
  }

  markMessageSnapshotsStaleExcept(conversationId: string | null | undefined) {
    const keptConversationId = normalizeText(conversationId);
    for (const conversation of this.recordsById.value.values()) {
      if (conversation.id.value !== keptConversationId) {
        conversation.messages.markSnapshotStale();
      }
    }
  }

  private visibleListRecords() {
    return this.sortedListIds(
      this.conversationData.listedConversationIds.value
    ).flatMap(conversationId => {
      const conversation = this.recordsById.value.get(conversationId);
      return conversation &&
        conversation.isListed.value &&
        !conversation.deleted.value &&
        conversation.hasInfo.value
        ? [conversation]
        : [];
    });
  }

  private sortedListIds(listIds: string[]) {
    return sortConversations(
      uniqueNormalizedIds(listIds).flatMap(listId => {
        const conversation = this.recordsById.value.get(listId);
        return conversation &&
          conversation.isListed.value &&
          !conversation.deleted.value &&
          conversation.hasInfo.value
          ? [conversation.info.value]
          : [];
      })
    ).map(conversation => conversation.conversationId);
  }

  private datasetConversationIds() {
    return uniqueNormalizedIds([
      ...this.shellIds.value,
      ...this.conversationData.conversationInfoById.value.keys(),
      ...this.conversationData.listedConversationIds.value,
      ...this.conversationData.deletedConversationIds.value,
      ...this.conversationData.messagesByConversationId.value.keys(),
      ...this.conversationData.createdMessagesByConversationId.value.keys(),
      ...this.conversationData.draftsByConversationId.value.keys(),
    ]);
  }

  private recordFor(conversationId: string) {
    let conversation = this.recordRegistry.get(conversationId);
    if (!conversation) {
      conversation = Conversation.shell(
        conversationId,
        this.conversationData,
        this.activityData
      );
      this.recordRegistry.set(conversationId, conversation);
    }
    return conversation;
  }
}
